/**
 * NChargi - Recharge Mobile App
 * Crypto payments + Quick recharge functionality
 */
(function() {
  'use strict';

  // ═══════════════════════════════════════════════════════════
  // Utilities
  // ═══════════════════════════════════════════════════════════
  const Utils = {
    copyToClipboard(text) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); return Promise.resolve(); }
      catch (err) { return Promise.reject(err); }
      finally { document.body.removeChild(textarea); }
    },

    showToast(title, message, type = 'success') {
      const container = document.querySelector('.toast-container');
      if (!container) return;
      const toast = document.createElement('div');
      toast.className = `toast toast-${type}`;
      const icons = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };
      toast.innerHTML = `
        <div class="toast-icon">${icons[type]}</div>
        <div class="toast-content">
          <div class="toast-title">${title}</div>
          ${message ? `<div class="toast-message">${message}</div>` : ''}
        </div>
      `;
      container.appendChild(toast);
      setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(100%)';
        setTimeout(() => toast.remove(), 300);
      }, 4000);
    },

    debounce(func, wait) {
      let timeout;
      return function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    },

    validatePhone(phone) {
      return /^(\+212|0)[5-7]\d{8}$/.test(phone.replace(/\s/g, ''));
    },

    formatMAD(amount) {
      return new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(amount);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // Mobile Menu
  // ═══════════════════════════════════════════════════════════
  class MobileMenu {
    constructor() {
      this.menu = document.querySelector('.mobile-menu');
      this.toggle = document.querySelector('[data-mobile-toggle]');
      if (!this.menu || !this.toggle) return;
      
      this.toggle.addEventListener('click', () => {
        this.menu.classList.toggle('active');
        document.body.style.overflow = this.menu.classList.contains('active') ? 'hidden' : '';
      });

      document.addEventListener('click', (e) => {
        if (this.menu.classList.contains('active') && !this.menu.contains(e.target) && !this.toggle.contains(e.target)) {
          this.menu.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
    }
  }

  // ═══════════════════════════════════════════════════════════
  // AJAX Cart
  // ═══════════════════════════════════════════════════════════
  class AjaxCart {
    constructor() {
      this.drawer = document.querySelector('.cart-drawer');
      this.overlay = document.querySelector('.cart-drawer-overlay');
      this.closeBtn = document.querySelector('.cart-drawer-close');
      this.countEls = document.querySelectorAll('.cart-count');
      
      if (!this.drawer) return;

      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());

      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-cart-toggle]')) {
          e.preventDefault();
          this.toggle();
        }
      });

      document.addEventListener('submit', (e) => {
        if (e.target.matches('form[action*="/cart/add"]')) {
          e.preventDefault();
          this.addFromForm(e.target);
        }
      });

      this.updateCount();
    }

    async addFromForm(form) {
      const formData = new FormData(form);
      const id = formData.get('id');
      const quantity = parseInt(formData.get('quantity')) || 1;
      const properties = {};

      for (let [key, value] of formData.entries()) {
        if (key.startsWith('properties[')) {
          const propName = key.match(/properties\[(.+)\]/)[1];
          properties[propName] = value;
        }
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, quantity, properties })
        });

        if (res.ok) {
          const cart = await res.json();
          this.updateUI(cart);
          this.open();
          Utils.showToast('Ajouté au panier', 'Article ajouté avec succès', 'success');
        } else {
          const err = await res.json();
          Utils.showToast('Erreur', err.description || 'Impossible d\'ajouter au panier', 'error');
        }
      } catch (err) {
        Utils.showToast('Erreur', 'Problème de connexion', 'error');
      }
    }

    async updateItem(key, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        const cart = await res.json();
        this.updateUI(cart);
        if (quantity === 0) Utils.showToast('Retiré', 'Article retiré du panier', 'info');
      } catch (err) {
        Utils.showToast('Erreur', 'Impossible de mettre à jour', 'error');
      }
    }

    updateUI(cart) {
      this.updateItems(cart.items);
      this.updateSubtotal(cart.total_price);
      this.updateCount();
    }

    updateItems(items) {
      const container = document.querySelector('.cart-drawer-items');
      if (!container) return;

      if (items.length === 0) {
        container.innerHTML = '<div class="cart-drawer-empty"><p>Votre panier est vide</p><a href="/collections/all" class="btn btn-primary mt-3">Voir les recharges</a></div>';
        return;
      }

      container.innerHTML = items.map(item => `
        <div class="cart-item">
          <div class="cart-item-details">
            <div class="cart-item-title">${item.product_title}</div>
            ${item.variant_title ? `<div class="cart-item-variant">${item.variant_title}</div>` : ''}
            ${Object.keys(item.properties || {}).length > 0 ? `
              <div class="cart-item-properties">
                ${Object.entries(item.properties).map(([k, v]) => `<div><strong>${k}:</strong> ${v}</div>`).join('')}
              </div>` : ''}
            <div class="cart-item-price">${this.formatMoney(item.line_price)}</div>
            <div class="cart-item-quantity">
              <button class="btn btn-sm btn-outline" onclick="window.cart.updateItem('${item.key}', ${item.quantity - 1})">−</button>
              <span>${item.quantity}</span>
              <button class="btn btn-sm btn-outline" onclick="window.cart.updateItem('${item.key}', ${item.quantity + 1})">+</button>
              <button class="btn btn-sm" style="color:#dc3545;margin-left:auto" onclick="window.cart.updateItem('${item.key}', 0)">Retirer</button>
            </div>
          </div>
        </div>
      `).join('');
    }

    formatMoney(cents) {
      return (cents / 100).toFixed(2) + ' DH';
    }

    updateSubtotal(totalPrice) {
      const el = document.querySelector('.cart-drawer-subtotal-value');
      if (el) el.textContent = this.formatMoney(totalPrice);
    }

    updateCount() {
      fetch('/cart.js').then(r => r.json()).then(cart => {
        const count = cart.item_count;
        this.countEls.forEach(el => {
          el.textContent = count > 0 ? count : '';
          el.style.display = count > 0 ? 'flex' : 'none';
        });
      });
    }

    open() {
      this.drawer.classList.add('active');
      this.overlay?.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.drawer.classList.remove('active');
      this.overlay?.classList.remove('active');
      document.body.style.overflow = '';
    }

    toggle() {
      this.drawer.classList.contains('active') ? this.close() : this.open();
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Quick Recharge Form
  // ═══════════════════════════════════════════════════════════
  class QuickRecharge {
    constructor() {
      document.querySelectorAll('.quick-recharge-form').forEach(form => {
        form.addEventListener('submit', (e) => this.handleSubmit(e, form));
      });

      // Phone input formatting
      document.querySelectorAll('input[name*="phone"]').forEach(input => {
        input.addEventListener('input', function() {
          this.value = this.value.replace(/[^\d+\s]/g, '');
        });
      });
    }

    handleSubmit(e, form) {
      const phone = form.querySelector('input[name*="phone"]');
      if (phone && !Utils.validatePhone(phone.value)) {
        e.preventDefault();
        Utils.showToast('Numéro invalide', 'Veuillez entrer un numéro marocain valide', 'error');
        phone.focus();
        return false;
      }
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Crypto Rates
  // ═══════════════════════════════════════════════════════════
  class CryptoRates {
    constructor() {
      this.rates = {};
      this.fetchRates();
      setInterval(() => this.fetchRates(), 60000); // Refresh every minute
    }

    async fetchRates() {
      try {
        const res = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,tether,litecoin,binancecoin,solana&vs_currencies=mad');
        const data = await res.json();
        
        this.rates = {
          BTC: data.bitcoin?.mad || 0,
          ETH: data.ethereum?.mad || 0,
          USDT: data.tether?.mad || 10,
          LTC: data.litecoin?.mad || 0,
          BNB: data.binancecoin?.mad || 0,
          SOL: data.solana?.mad || 0
        };

        this.updateDisplay();
      } catch (err) {
        console.error('Failed to fetch crypto rates:', err);
      }
    }

    updateDisplay() {
      document.querySelectorAll('[data-crypto-rate]').forEach(el => {
        const symbol = el.dataset.cryptoRate;
        if (this.rates[symbol]) {
          el.textContent = `1 ${symbol} = ${this.rates[symbol].toLocaleString('fr-MA')} MAD`;
        }
      });
    }

    convertToCrypto(madAmount, symbol) {
      if (!this.rates[symbol]) return null;
      return (madAmount / this.rates[symbol]).toFixed(8);
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Copy to Clipboard
  // ═══════════════════════════════════════════════════════════
  class CopyToClipboard {
    constructor() {
      document.addEventListener('click', (e) => {
        const target = e.target.closest('[data-copy]');
        if (!target) return;
        e.preventDefault();
        Utils.copyToClipboard(target.dataset.copy).then(() => {
          const original = target.textContent;
          target.textContent = '✓ Copié!';
          setTimeout(() => target.textContent = original, 2000);
          Utils.showToast('Copié!', 'Adresse copiée dans le presse-papiers', 'success');
        });
      });
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Promo Countdown
  // ═══════════════════════════════════════════════════════════
  class PromoCountdown {
    constructor() {
      document.querySelectorAll('[data-countdown]').forEach(el => {
        const endDate = new Date(el.dataset.countdown).getTime();
        if (!endDate || isNaN(endDate)) return;
        
        const update = () => {
          const now = Date.now();
          const diff = endDate - now;
          if (diff <= 0) { el.textContent = 'Expirée'; return; }
          
          const days = Math.floor(diff / 86400000);
          const hours = Math.floor((diff % 86400000) / 3600000);
          const mins = Math.floor((diff % 3600000) / 60000);
          el.textContent = `${days}j ${hours}h ${mins}m`;
        };
        
        update();
        setInterval(update, 60000);
      });
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Initialize
  // ═══════════════════════════════════════════════════════════
  document.addEventListener('DOMContentLoaded', () => {
    window.cart = new AjaxCart();
    new MobileMenu();
    new QuickRecharge();
    new CryptoRates();
    new CopyToClipboard();
    new PromoCountdown();
    
    console.log('🚀 NChargi theme loaded');
  });

})();
